import { html } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { RaygunElement } from '@src/js/common/rg-element.js'

const TransitionStates = {
  enterFrom:    { eventName: 'enter-start',   className: 'enter-from' },
  enterActive:  { eventName: 'enter-run',     className: 'enter-active' },
  enterTo:      { eventName: 'enter-end',     className: 'enter-to' },
  leaveFrom:    { eventName: 'leave-start',   className: 'leave-from' },
  leaveActive:  { eventName: 'leave-run',     className: 'leave-active' },
  leaveTo:      { eventName: 'leave-end',     className: 'leave-to' },
}

@customElement('rg-transition')
export class RgTransition extends RaygunElement {

  timerIds = []
  frameIds = []

  @property({ type: Boolean, reflect: true })
  active = false

  @property({ type: Boolean, reflect: true })
  autostart = false

  @property({ type: String, attribute: 'enter-from-class' })
  enterFromClass = ''

  @property({ type: String, attribute: 'enter-active-class' })
  enterActiveClass = ''

  @property({ type: String, attribute: 'enter-to-class' })
  enterToClass = ''

  @property({ type: String, attribute: 'leave-from-class' })
  leaveFromClass = ''

  @property({ type: String, attribute: 'leave-active-class' })
  leaveActiveClass = ''

  @property({ type: String, attribute: 'leave-to-class' })
  leaveToClass = ''

  @state({ type: Boolean })
  useScopeAttribute = true

  async enterTransition() {
    const { enterFrom, enterActive, enterTo } = TransitionStates
    this.runTasks([
      () => this.setTransitionState(enterFrom),
      () => this.nextFrame(),
      () => this.setTransitionState(enterActive),
      () => this.waitForTransition(),
      () => this.setTransitionState(enterTo)
    ])
  }

  async leaveTransition() {
    const { leaveFrom, leaveActive, leaveTo } = TransitionStates
    this.runTasks([
      () => this.setTransitionState(leaveFrom),
      () => this.nextFrame(),
      () => this.setTransitionState(leaveActive),
      () => this.waitForTransition(),
      () => this.setTransitionState(leaveTo)
    ])
  }

  setTransitionState(state) {
    Object.keys(TransitionStates).forEach(key => {
      const currentState = TransitionStates[key]
      const isActive = currentState === state
      const className = this[`${key}Class`] || currentState.className
      if (className) {
        this.classList.toggle(className, isActive)
      }
      if (isActive) {
        this.notify(currentState.eventName)
      }
    })
  }

  notify(eventName) {
    this.dispatchEvent(new Event(eventName, {
      bubbles: true,
      composed: true,
      cancelable: true,
    }))
  }

  async nextFrame() {
    const { frameIds } = this
    return new Promise(resolve => {
      frameIds.push(requestAnimationFrame(() => {
        frameIds.push(requestAnimationFrame(resolve))
      }))
    })
  }

  getStyleDuration(style) {
    const str = style.transitionDuration
    const duration = global.parseFloat( str || 0)
    const mult = str.includes('ms') ? 1 : 1000
    return duration * mult
  }

  async waitForTransition() {
    const { timerIds } = this
    const style = global.getComputedStyle(this)
    const duration = this.getStyleDuration(style)
    return new Promise(resolve => {
      timerIds.push(global.setTimeout(resolve, duration))
    })
  }

  cancelTasks() {
    this.timerIds.forEach(id => global.clearTimeout(id))
    this.frameIds.forEach(id => global.cancelAnimationFrame(id))
    this.timerIds = [];
    this.frameIds = [];
  }

  async runTasks(tasks = []) {
    this.cancelTasks()
    for (const task of tasks) {
      await Promise.resolve(task());
    }
  }

  connectedCallback() {
    super.connectedCallback()
    if (this.autostart) {
      this.active = true
      this.enterTransition()
    }
  }

  update(changedProperties) {
    if (this.hasUpdated) {
      this.active ? this.enterTransition() : this.leaveTransition()
    }
    super.update(changedProperties)
  }

  render() {
    const scopedClass = `rg-transition[${this.scopeId}]`
    return html`
      <style>
        ${scopedClass} {
          display: block;
          transition-property: all;
          transition-duration: 0.3s;
          transition-timing-function: ease-in-out;
        }
      </style>
      ${this.slot()}
    `
  }
}
